/**
 * EnumValuesEditor — editor de la lista de valores enum de una columna
 * `type='enum'` de un structured_table (Doc 24 §3 Fase 10).
 *
 * Shape v2 (`EnumValue`):
 *   ```
 *   { value: 'leadership', i18n: { es: { label: 'Liderazgo' },
 *                                  en: { label: 'Leadership' } } }
 *   ```
 *
 * UX simplificada — value + 2 labels (es/en) side-by-side, sin sub-tabs
 * (la fila ya tiene 4 inputs visibles, agregar tabs duplica clicks). Si
 * más adelante hay >3 idiomas se puede degradar a tabs.
 *
 * Validación inline:
 *   - No permite dos valores con la misma `value` (warning visual al
 *     editar; el value en sí queda en el estado para no perderse).
 *   - No emite ningún auto-rename — el author es responsable de fijar
 *     el duplicado.
 */

import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { EnumValue } from '../../../api/templates'

export interface EnumValuesEditorProps {
  values: EnumValue[]
  /** Locales activos del documento (`Object.keys(definition.i18n)`) — el
   * editor genera una columna `label-{loc}` por cada uno. Si solo está
   * `["es"]`, solo se ve la columna ES (no la EN como antes hardcoded). */
  activeLocales: string[]
  readOnly?: boolean
  onChange(next: EnumValue[]): void
  /** ID estable para tests / a11y. */
  testId?: string | undefined
}

function newEnumValue(existing: EnumValue[], locales: string[]): EnumValue {
  const taken = new Set(existing.map((v) => v.value))
  let n = existing.length + 1
  while (taken.has(`option_${n}`)) n += 1
  const i18n: Record<string, { label: string }> = {}
  for (const loc of locales) i18n[loc] = { label: '' }
  return { value: `option_${n}`, i18n }
}

export function EnumValuesEditor({
  values,
  activeLocales,
  readOnly = false,
  onChange,
  testId,
}: EnumValuesEditorProps) {
  const { t } = useTranslation('templates')

  // Detectar duplicados (excluyendo el propio idx).
  const duplicateIdxs = new Set<number>()
  const counts = new Map<string, number>()
  values.forEach((v) => counts.set(v.value, (counts.get(v.value) ?? 0) + 1))
  values.forEach((v, i) => {
    if ((counts.get(v.value) ?? 0) > 1) duplicateIdxs.add(i)
  })

  function updateAt(idx: number, patch: Partial<EnumValue>) {
    const next = values.map((v, i) =>
      i === idx ? ({ ...v, ...patch } as EnumValue) : v,
    )
    onChange(next)
  }

  function updateLabel(idx: number, locale: string, label: string) {
    const current = values[idx]
    if (!current) return
    const nextI18n = {
      ...current.i18n,
      [locale]: { ...(current.i18n[locale] ?? { label: '' }), label },
    }
    updateAt(idx, { i18n: nextI18n })
  }

  function removeAt(idx: number) {
    onChange(values.filter((_, i) => i !== idx))
  }

  function addNew() {
    onChange([...values, newEnumValue(values, activeLocales)])
  }

  return (
    <div data-testid={testId} className="space-y-2">
      <div>
        <p className="text-xs font-medium text-ink-muted">
          {t('designer.editor.table_config_v2.enum_values_label')}
        </p>
        <p className="text-[11px] text-ink-subtle italic mt-0.5">
          {t('designer.editor.table_config_v2.enum_values_helper')}
        </p>
      </div>

      {values.length === 0 ? (
        <p
          className="text-[11px] text-critical italic"
          data-testid={testId ? `${testId}-empty-warning` : undefined}
        >
          {t('designer.editor.table_config_v2.enum_requires_values')}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {values.map((v, idx) => {
            const isDup = duplicateIdxs.has(idx)
            // Grid templated dinámicamente: value-col + N label-cols + remove.
            const gridCols = `1fr ${activeLocales.map(() => '1fr').join(' ')} auto`
            return (
              <li
                key={idx}
                className="grid gap-2 items-start"
                style={{ gridTemplateColumns: gridCols }}
                data-testid={testId ? `${testId}-row-${idx}` : undefined}
              >
                <label className="block">
                  <span className="text-[10px] text-ink-subtle font-mono">
                    {t('designer.editor.table_config_v2.enum_value_label')}
                  </span>
                  <input
                    type="text"
                    value={v.value}
                    onChange={(e) => updateAt(idx, { value: e.target.value })}
                    disabled={readOnly}
                    pattern="[a-z][a-z0-9_]*"
                    data-testid={testId ? `${testId}-value-${idx}` : undefined}
                    className={
                      isDup
                        ? 'mt-1 w-full bg-canvas border border-critical/60 rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:border-critical disabled:opacity-60'
                        : 'mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-xs font-mono focus:outline-none focus:border-brand disabled:opacity-60'
                    }
                  />
                </label>
                {activeLocales.map((loc) => (
                  <label key={loc} className="block">
                    <span className="text-[10px] text-ink-subtle">
                      {t(
                        `designer.editor.table_config_v2.enum_label_${loc}`,
                        {
                          defaultValue: `Label (${loc})`,
                        },
                      )}
                    </span>
                    <input
                      type="text"
                      value={v.i18n[loc]?.label ?? ''}
                      onChange={(e) =>
                        updateLabel(idx, loc, e.target.value)
                      }
                      disabled={readOnly}
                      data-testid={
                        testId ? `${testId}-label-${loc}-${idx}` : undefined
                      }
                      className="mt-1 w-full bg-canvas border border-line rounded-md px-2 py-1 text-xs focus:outline-none focus:border-brand disabled:opacity-60"
                    />
                  </label>
                ))}
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    aria-label={t(
                      'designer.editor.table_config_v2.remove_enum_value',
                    ) as string}
                    className="mt-5 text-ink-subtle hover:text-critical p-1"
                    data-testid={testId ? `${testId}-remove-${idx}` : undefined}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <span />
                )}
                {isDup ? (
                  <p
                    className="-mt-1 text-[10px] text-critical"
                    style={{ gridColumn: `span ${activeLocales.length + 2}` }}
                    data-testid={
                      testId ? `${testId}-duplicate-warning-${idx}` : undefined
                    }
                  >
                    {t(
                      'designer.editor.table_config_v2.duplicate_enum_value_warning',
                    )}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {!readOnly ? (
        <button
          type="button"
          onClick={addNew}
          data-testid={testId ? `${testId}-add` : undefined}
          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-line text-ink hover:bg-surface-muted"
        >
          <Plus className="w-3.5 h-3.5" />
          {t('designer.editor.table_config_v2.add_enum_value')}
        </button>
      ) : null}
    </div>
  )
}
